import { useEffect, useState } from "react";
import { ClipboardList, Edit3, Eye, Plus, Search, Trash2 } from "lucide-react";
import http from "../api/http";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";
import { useServerPagination } from "../utils/usePagination";
import { formatDate } from "../utils/format";
import { consumirIntencionNuevo } from "../utils/navigationIntent";

const estadoInicial = (tipoCliente = "Paciente") => ({
  tipoCliente,
  tipoDocumento: "DNI",
  numeroDocumento: "",
  nombre: "",
  apellido: "",
  fechaNacimiento: "",
  telefono: "",
  email: "",
  direccion: "",
  barrio: "",
  ciudad: "",
  departamento: "",
  pais: "Argentina",
  obraSocial: "",
  numeroAfiliado: "",
  matricula: "",
  especialidad: "",
  consultorio: "",
  activo: true,
  rol: "cliente",
});

const tiposDocumento = ["DNI", "CUIT", "Pasaporte"];

const nombreCliente = (cliente) =>
  `${cliente?.nombre || cliente?.nombreTitular || ""} ${
    cliente?.apellido || cliente?.apellidoTitular || ""
  }`.trim() ||
  cliente?.razonSocial ||
  "";

const valorFechaInput = (value) => {
  if (!value) return "";
  const fecha = new Date(value);
  if (Number.isNaN(fecha.getTime())) return "";
  return fecha.toISOString().slice(0, 10);
};

const cantidadTotalItem = (item) =>
  Number(item?.kilosTotales || 0) ||
  Number(item?.kilosPorPaquete || 0) * Number(item?.cantidadPaquetes || 0);

export default function Clientes({ tipoCliente = "Paciente" }) {
  const esPaciente = tipoCliente === "Paciente";
  const tipoPlural = esPaciente ? "pacientes" : "profesionales";
  const [clientes, setClientes] = useState([]);
  const [formulario, setFormulario] = useState(() => estadoInicial(tipoCliente));
  const [clienteEditando, setClienteEditando] = useState(null);
  const [clienteDetalle, setClienteDetalle] = useState(null);
  const [historialCliente, setHistorialCliente] = useState(null);
  const [recetas, setRecetas] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [errorHistorial, setErrorHistorial] = useState("");
  const [modalAbierto, setModalAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const paginacion = useServerPagination();

  const obtenerClientes = async () => {
    try {
      setCargando(true);
      setError("");
      const { data } = await http.get("/clientes", {
        params: {
          tipoCliente,
          q: busqueda || undefined,
          page: paginacion.page,
          limit: paginacion.pageSize,
        },
      });
      setClientes(Array.isArray(data?.items) ? data.items : []);
      paginacion.applyResponse(data);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron cargar los clientes.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    setClientes([]);
    setFormulario(estadoInicial(tipoCliente));
    setClienteEditando(null);
    setClienteDetalle(null);
    setHistorialCliente(null);
    if (consumirIntencionNuevo()) setModalAbierto(true);
  }, [tipoCliente]);

  useEffect(() => {
    const timeout = window.setTimeout(obtenerClientes, 250);
    return () => window.clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCliente, busqueda, paginacion.page, paginacion.pageSize]);

  const clientesFiltrados = clientes;
  const paginacionRecetas = useServerPagination();

  const limpiarMensajes = () => {
    setMensaje("");
    setError("");
  };

  const abrirCrear = () => {
    limpiarMensajes();
    setClienteEditando(null);
    setFormulario(estadoInicial(tipoCliente));
    setModalAbierto(true);
  };

  const abrirEditar = (cliente) => {
    limpiarMensajes();
    setClienteEditando(cliente);
    setFormulario({
      ...estadoInicial(tipoCliente),
      ...cliente,
      tipoCliente,
      nombre: cliente.nombre || cliente.nombreTitular || "",
      apellido: cliente.apellido || cliente.apellidoTitular || "",
      telefono: cliente.telefono || cliente.telefonoTitular || "",
      fechaNacimiento: valorFechaInput(cliente.fechaNacimiento),
      activo: cliente.activo ?? true,
      rol: "cliente",
    });
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setClienteEditando(null);
    setFormulario(estadoInicial(tipoCliente));
    setError("");
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setError("");
    setFormulario((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validarFormulario = () => {
    if (!formulario.nombre.trim() || !formulario.apellido.trim()) {
      setError("El nombre y el apellido son obligatorios.");
      return false;
    }
    if (!formulario.numeroDocumento.trim()) {
      setError("El numero de documento es obligatorio.");
      return false;
    }
    if (formulario.email && !/.+@.+\..+/.test(formulario.email)) {
      setError("El email ingresado no es valido.");
      return false;
    }
    return true;
  };

  const guardarCliente = async (event) => {
    event.preventDefault();
    if (!validarFormulario()) return;

    try {
      setCargando(true);
      setError("");
      const payload = {
        ...formulario,
        tipoCliente,
        razonSocial: `${formulario.nombre} ${formulario.apellido}`.trim(),
        rol: "cliente",
      };

      if (clienteEditando) {
        await http.put(`/clientes/${clienteEditando._id}`, payload);
        setMensaje("Cliente actualizado correctamente.");
      } else {
        await http.post("/clientes", payload);
        setMensaje("Cliente creado correctamente.");
      }

      setModalAbierto(false);
      setClienteEditando(null);
      setFormulario(estadoInicial(tipoCliente));
      await obtenerClientes();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo guardar el cliente.");
    } finally {
      setCargando(false);
    }
  };

  const eliminarCliente = async (id) => {
    if (!window.confirm("Seguro que queres eliminar este cliente?")) return;
    try {
      setCargando(true);
      setError("");
      await http.delete(`/clientes/${id}`);
      setMensaje("Cliente eliminado correctamente.");
      await obtenerClientes();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo eliminar el cliente.");
    } finally {
      setCargando(false);
    }
  };

  const abrirHistorial = (cliente) => {
    setHistorialCliente(cliente);
    setRecetas([]);
    setErrorHistorial("");
    paginacionRecetas.resetPage();
  };

  useEffect(() => {
    if (!historialCliente?._id) return undefined;
    let vigente = true;
    const cargarHistorial = async () => {
      setCargandoHistorial(true);
      try {
        const { data } = await http.get(`/clientes/${historialCliente._id}/recetas-medicas`, {
          params: { page: paginacionRecetas.page, limit: paginacionRecetas.pageSize },
        });
        if (!vigente) return;
        setRecetas(Array.isArray(data?.items) ? data.items : []);
        paginacionRecetas.applyResponse(data);
      } catch (err) {
        if (vigente) setErrorHistorial(err.response?.data?.error || "No se pudo cargar el historial de recetas.");
      } finally {
        if (vigente) setCargandoHistorial(false);
      }
    };
    cargarHistorial();
    return () => { vigente = false; };
  }, [historialCliente?._id, paginacionRecetas.page, paginacionRecetas.pageSize]);

  return (
    <div>
      <div className="page-header actions-only">
        <div className="header-actions">
          <button className="btn btn-primary" onClick={abrirCrear}>
            <Plus size={18} />
            Nuevo {tipoCliente.toLowerCase()}
          </button>
        </div>
      </div>

      {mensaje && <div className="alert alert-ok">{mensaje}</div>}
      {error && !modalAbierto && <div className="alert alert-error">{error}</div>}

      <section className="panel">
        <div className="toolbar">
          <div>
            <h2>Clientes {tipoPlural}</h2>
            <p>Total: {paginacion.totalItems}</p>
          </div>
          <div className="search">
            <Search size={17} />
            <input
              placeholder={`Buscar ${tipoPlural} por nombre, documento o ${
                esPaciente ? "obra social" : "matricula"
              }...`}
              value={busqueda}
              onChange={(event) => {
                setBusqueda(event.target.value);
                paginacion.resetPage();
              }}
            />
          </div>
        </div>

        {cargando ? (
          <div className="loading-state">Cargando clientes {tipoPlural}...</div>
        ) : clientesFiltrados.length === 0 ? (
          <div className="empty-state">No hay clientes {tipoPlural} para mostrar.</div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Nombre y apellido</th>
                  <th>Documento</th>
                  <th>Telefono</th>
                  <th>Email</th>
                  {esPaciente ? (
                    <>
                      <th>Obra social</th>
                      <th>Nro. afiliado</th>
                    </>
                  ) : (
                    <>
                      <th>Matricula</th>
                      <th>Especialidad</th>
                      <th>Consultorio</th>
                    </>
                  )}
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clientesFiltrados.map((cliente) => (
                  <tr key={cliente._id}>
                    <td>{cliente.codigoCliente || "-"}</td>
                    <td><strong>{nombreCliente(cliente) || "-"}</strong></td>
                    <td>{`${cliente.tipoDocumento || ""} ${cliente.numeroDocumento || ""}`.trim() || "-"}</td>
                    <td>{cliente.telefono || cliente.telefonoTitular || "-"}</td>
                    <td>{cliente.email || "-"}</td>
                    {esPaciente ? (
                      <>
                        <td>{cliente.obraSocial || "-"}</td>
                        <td>{cliente.numeroAfiliado || "-"}</td>
                      </>
                    ) : (
                      <>
                        <td>{cliente.matricula || "-"}</td>
                        <td>{cliente.especialidad || "-"}</td>
                        <td>{cliente.consultorio || "-"}</td>
                      </>
                    )}
                    <td>
                      <span className={`badge ${cliente.activo ? "ok" : "off"}`}>
                        {cliente.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        {esPaciente && (
                          <button
                            className="btn action-icon-button"
                            type="button"
                            title="Historial de recetas"
                            aria-label="Historial de recetas"
                            onClick={() => abrirHistorial(cliente)}
                          >
                            <ClipboardList size={16} />
                          </button>
                        )}
                        <button
                          className="btn action-icon-button"
                          type="button"
                          title="Detalle"
                          aria-label="Detalle"
                          onClick={() => setClienteDetalle(cliente)}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn action-icon-button"
                          type="button"
                          title="Editar"
                          aria-label="Editar"
                          onClick={() => abrirEditar(cliente)}
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          className="btn btn-danger action-icon-button"
                          type="button"
                          title="Eliminar"
                          aria-label="Eliminar"
                          onClick={() => eliminarCliente(cliente._id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            <Pagination
              page={paginacion.page}
              pageSize={paginacion.pageSize}
              totalItems={paginacion.totalItems}
              totalPages={paginacion.totalPages}
              onPageChange={paginacion.setPage}
              onPageSizeChange={paginacion.setPageSize}
            />
          </>
        )}
      </section>

      <Modal
        abierto={modalAbierto}
        titulo={clienteEditando ? "Editar cliente" : `Cargar nuevo ${tipoCliente.toLowerCase()}`}
        subtitulo={
          clienteEditando
            ? `Modificando: ${nombreCliente(clienteEditando)}`
            : `Completa los datos personales del ${tipoCliente.toLowerCase()}.`
        }
        size="wide"
        onClose={cerrarModal}
        footer={
          <>
            <button className="btn" type="button" onClick={cerrarModal}>Cancelar</button>
            <button className="btn btn-primary" form="cliente-form" type="submit" disabled={cargando}>
              {clienteEditando ? "Actualizar cliente" : "Guardar cliente"}
            </button>
          </>
        }
      >
        {error && <div className="alert alert-error">{error}</div>}
        <form id="cliente-form" onSubmit={guardarCliente}>
          <section className="form-section">
            <h3>Informacion personal</h3>
            <div className="form-grid">
              <Select label="Tipo cliente" name="tipoCliente" value={tipoCliente} options={[tipoCliente]} disabled />
              <Select label="Tipo documento" name="tipoDocumento" value={formulario.tipoDocumento} onChange={handleChange} options={tiposDocumento} />
              <Field label="Numero documento *" name="numeroDocumento" value={formulario.numeroDocumento} onChange={handleChange} />
              <Field label="Nombre *" name="nombre" value={formulario.nombre} onChange={handleChange} />
              <Field label="Apellido *" name="apellido" value={formulario.apellido} onChange={handleChange} />
              <Field label="Fecha de nacimiento" type="date" name="fechaNacimiento" value={formulario.fechaNacimiento} onChange={handleChange} />
            </div>
          </section>

          <section className="form-section">
            <h3>Contacto y domicilio</h3>
            <div className="form-grid">
              <Field label="Telefono" name="telefono" value={formulario.telefono} onChange={handleChange} />
              <Field label="Email" type="text" inputMode="email" name="email" value={formulario.email} onChange={handleChange} />
              <Field label="Direccion" name="direccion" value={formulario.direccion} onChange={handleChange} />
              <Field label="Barrio" name="barrio" value={formulario.barrio} onChange={handleChange} />
              <Field label="Ciudad" name="ciudad" value={formulario.ciudad} onChange={handleChange} />
              <Field label="Departamento" name="departamento" value={formulario.departamento} onChange={handleChange} />
              <Field label="Pais" name="pais" value={formulario.pais} onChange={handleChange} />
            </div>
          </section>

          <section className="form-section">
            <h3>{esPaciente ? "Datos del paciente" : "Datos profesionales"}</h3>
            <div className="form-grid">
              {esPaciente ? (
                <>
                  <Field label="Obra social" name="obraSocial" value={formulario.obraSocial} onChange={handleChange} />
                  <Field label="Numero de afiliado" name="numeroAfiliado" value={formulario.numeroAfiliado} onChange={handleChange} />
                </>
              ) : (
                <>
                  <Field label="Matricula" name="matricula" value={formulario.matricula} onChange={handleChange} />
                  <Field label="Especialidad" name="especialidad" value={formulario.especialidad} onChange={handleChange} />
                  <Field label="Consultorio" name="consultorio" value={formulario.consultorio} onChange={handleChange} />
                </>
              )}
            </div>
          </section>

          <section className="form-section">
            <h3>Estado</h3>
            <label className="field-check">
              <input type="checkbox" name="activo" checked={formulario.activo} onChange={handleChange} />
              Cliente activo
            </label>
          </section>
        </form>
      </Modal>

      <Modal
        abierto={Boolean(clienteDetalle)}
        titulo="Detalle del cliente"
        subtitulo={nombreCliente(clienteDetalle)}
        size="wide"
        onClose={() => setClienteDetalle(null)}
        footer={<button className="btn" type="button" onClick={() => setClienteDetalle(null)}>Cerrar</button>}
      >
        {clienteDetalle && (
          <div className="order-detail">
            <section className="order-detail-summary">
              <DetalleDato label="Codigo" value={clienteDetalle.codigoCliente} />
              <DetalleDato label="Nombre y apellido" value={nombreCliente(clienteDetalle)} />
              <DetalleDato label="Tipo de cliente" value={clienteDetalle.tipoCliente} />
              <DetalleDato label="Documento" value={`${clienteDetalle.tipoDocumento || ""} ${clienteDetalle.numeroDocumento || ""}`.trim()} />
              <DetalleDato label="Fecha de nacimiento" value={formatDate(clienteDetalle.fechaNacimiento)} />
              <DetalleDato label="Telefono" value={clienteDetalle.telefono || clienteDetalle.telefonoTitular} />
              <DetalleDato label="Email" value={clienteDetalle.email} />
              <DetalleDato label="Direccion" value={clienteDetalle.direccion} />
              <DetalleDato label="Barrio" value={clienteDetalle.barrio} />
              <DetalleDato label="Ciudad" value={clienteDetalle.ciudad} />
              <DetalleDato label="Departamento" value={clienteDetalle.departamento} />
              <DetalleDato label="Pais" value={clienteDetalle.pais} />
              {clienteDetalle.tipoCliente === "Paciente" ? (
                <>
                  <DetalleDato label="Obra social" value={clienteDetalle.obraSocial} />
                  <DetalleDato label="Numero de afiliado" value={clienteDetalle.numeroAfiliado} />
                </>
              ) : (
                <>
                  <DetalleDato label="Matricula" value={clienteDetalle.matricula} />
                  <DetalleDato label="Especialidad" value={clienteDetalle.especialidad} />
                  <DetalleDato label="Consultorio" value={clienteDetalle.consultorio} />
                </>
              )}
              <DetalleDato label="Estado" value={clienteDetalle.activo ? "Activo" : "Inactivo"} />
            </section>
          </div>
        )}
      </Modal>

      <Modal
        abierto={Boolean(historialCliente)}
        titulo="Historial de recetas medicas"
        subtitulo={nombreCliente(historialCliente)}
        size="wide"
        onClose={() => setHistorialCliente(null)}
        footer={<button className="btn" type="button" onClick={() => setHistorialCliente(null)}>Cerrar</button>}
      >
        {errorHistorial && <div className="alert alert-error">{errorHistorial}</div>}
        {cargandoHistorial ? (
          <div className="loading-state">Cargando historial de recetas...</div>
        ) : recetas.length === 0 ? (
          <div className="empty-state">El paciente todavia no tiene recetas medicas registradas.</div>
        ) : (
          <div className="trace-list">
            {recetas.map((receta) => (
              <article className="trace-card" key={receta.pedidoId}>
                <div className="trace-card-header">
                  <div>
                    <strong>{receta.codigoPedido || "Pedido sin codigo"}</strong>
                    <span className="subtext">Pedido: {formatDate(receta.fechaPedido)}</span>
                  </div>
                  <span className={`badge ${receta.estado === "finalizada" ? "ok" : "warn"}`}>
                    {receta.estado || "-"}
                  </span>
                </div>

                <section className="order-detail-summary">
                  <DetalleDato label="Medico" value={receta.medico} />
                  <DetalleDato label="Matricula" value={receta.matriculaMedico} />
                  <DetalleDato label="Emision de receta" value={formatDate(receta.fechaEmision)} />
                  <DetalleDato label="Finalizacion del pedido" value={formatDate(receta.fechaFinalizacion)} />
                  <DetalleDato label="Diagnostico" value={receta.diagnostico} />
                  <DetalleDato label="Observaciones de receta" value={receta.observacionesReceta} />
                  <DetalleDato label="Observaciones del pedido" value={receta.observacionesPedido} />
                </section>

                <section className="order-detail-section">
                  <h3>Productos recetados</h3>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th>SKU</th>
                          <th>Presentacion</th>
                          <th>Paquetes</th>
                          <th>Cantidad total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(receta.productos || []).map((producto, index) => (
                          <tr key={producto._id || `${receta.pedidoId}-${index}`}>
                            <td><strong>{producto.producto || "-"}</strong></td>
                            <td>{producto.codigoSku || "-"}</td>
                            <td>{Number(producto.kilosPorPaquete || 0).toLocaleString("es-AR")} {producto.unidadMedida || "kg"}</td>
                            <td>{Number(producto.cantidadPaquetes || 0).toLocaleString("es-AR")}</td>
                            <td>{cantidadTotalItem(producto).toLocaleString("es-AR")} {producto.unidadMedida || "kg"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </article>
            ))}
            <Pagination
              page={paginacionRecetas.page}
              pageSize={paginacionRecetas.pageSize}
              totalItems={paginacionRecetas.totalItems}
              totalPages={paginacionRecetas.totalPages}
              onPageChange={paginacionRecetas.setPage}
              onPageSizeChange={paginacionRecetas.setPageSize}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

function DetalleDato({ label, value }) {
  return (
    <div className="detail-item">
      <span>{label}</span>
      <strong>{value === undefined || value === null || value === "" ? "-" : value}</strong>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div className="field">
      <label>{label}</label>
      <input type="text" {...props} />
    </div>
  );
}

function Select({ label, options, ...props }) {
  return (
    <div className="field">
      <label>{label}</label>
      <select {...props}>
        <option value="">Seleccionar</option>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </div>
  );
}
